/**
 * Audit page section 3: the draft report, built in the Ryoma Word template
 * (apps/audit/public/ryoma-template.docx). Each export is recorded with its version block.
 * Founders write the recommendations and check every figure before it goes to the client.
 */
import { ENGINE_VERSION } from "@ryoma/engine";
import type { AuditRow, Db, UploadRow } from "../data/db.ts";
import { computeBaseline } from "../lib/baseline.ts";
import { renderReport } from "../lib/docx-report.ts";
import { IMPLEMENTATION_SHARE, RETAINER_CAP_SHARE, bandFor, retainerCap, suggestedImplementationFee } from "../lib/pricing.ts";
import { buildReportModel, buildVersionBlock, savingTotals } from "../lib/report-model.ts";
import { registerAuditSection } from "./audit.ts";
import { action, dropZone, date, deleteButton, h, mount, notice, table } from "./dom.ts";

const TEMPLATE_URL = "/ryoma-template.docx";
const STAMP_URL = "/checked-stamp.png";

/** A file deployed with the app, checked by its first bytes (a missing file can come back as index.html). */
async function bundled(url: string, magic: number[]): Promise<ArrayBuffer | null> {
  try {
    const res = await fetch(url, { cache: "no-cache" });
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    const b = new Uint8Array(buf.slice(0, magic.length));
    return magic.every((m, i) => b[i] === m) ? buf : null;
  } catch {
    return null;
  }
}
const ZIP = [0x50, 0x4b];
const PNG = [0x89, 0x50, 0x4e, 0x47];
const bundledTemplate = () => bundled(TEMPLATE_URL, ZIP);

function download(bytes: Uint8Array, fileName: string): void {
  const blob = new Blob([new Uint8Array(bytes)], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
  const url = URL.createObjectURL(blob);
  const a = h("a", { href: url, download: fileName });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** The last export's file name per audit, so the message survives the page refresh that follows. */
const lastExport = new Map<string, string>();

const safeName = (s: string) => s.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim().slice(0, 80);

async function reportSection({ db, audit, upload, refresh }: { db: Db; audit: AuditRow; upload: UploadRow | null; refresh: () => void }): Promise<HTMLElement> {
  const box = h("div", { class: "card" }, h("h2", null, "3. Draft report"));
  if (!upload) {
    box.append(notice("info", "Upload client data and run the levers first."));
    return box;
  }
  const [runs, reports, template, manual] = await Promise.all([db.latestLeverRuns(audit.id), db.listReports(audit.id), bundledTemplate(), db.manualLevers(audit.id)]);
  const current = (n: 1 | 2) => runs[n]?.dataset_hash === upload.dataset_hash;
  const sample = [...(runs[1]?.rate_cards ?? []), ...(runs[2]?.rate_cards ?? [])].some((r) => r.startsWith("sample-"));

  const tplFile = h("input", { type: "file", accept: ".docx", "aria-label": "Report template" });
  const out = h("div");
  const done = lastExport.get(audit.id);
  if (done) {
    lastExport.delete(audit.id);
    mount(out, notice("ok", `Draft downloaded: ${done}. Write the recommendations in the gold box, then check every figure before it goes to the client.`));
  }

  const exportBtn = action("Export draft report", async () => {
    let tpl = template;
    if (!tpl) {
      const f = tplFile.files?.[0];
      if (!f) throw new Error("Choose the Ryoma Word template first");
      tpl = await f.arrayBuffer();
    }
    // Read the latest figures now, so anything saved since the page loaded is included.
    const [runsNow, manualNow, auditNow, actionsNow, reportsNow, parcels, specs, stamp] = await Promise.all([
      db.latestLeverRuns(audit.id), db.manualLevers(audit.id), db.getAudit(audit.id), db.listActions(audit.id), db.listReports(audit.id),
      db.uploadParcels(upload.id), db.productSpecs(audit.id), bundled(STAMP_URL, PNG),
    ]);
    const block = buildVersionBlock(upload, runsNow, ENGINE_VERSION);
    const model = buildReportModel({
      audit: auditNow ?? audit, upload, runs: runsNow, manual: manualNow, actions: actionsNow, block, draftNumber: reportsNow.length + 1,
      baseline: computeBaseline(parcels, upload.date_from, upload.date_to, specs),
    });
    const bytes = await renderReport(tpl, model, { stamp });
    await db.saveReport(audit.id, block);
    const file = `${safeName(`Ryoma audit - ${audit.client_name} - ${block.generatedAt.slice(0, 10)}`)} DRAFT ${reportsNow.length + 1}.docx`;
    download(bytes, file);
    lastExport.set(audit.id, file);
    refresh();
  }, { primary: true, disabled: !current(1) });

  // Our fees, so the report can set the client's costs after the savings and all of our fees against today's.
  const perMonth = upload.date_from && upload.date_to
    ? Math.round((upload.parcel_count / (Math.round((Date.parse(upload.date_to) - Date.parse(upload.date_from)) / 864e5) + 1)) * 30.44)
    : upload.parcel_count;
  const band = bandFor(perMonth);
  const annual = savingTotals(upload, runs, manual).totalAnnual;
  const pounds = (p: number) => `£${(p / 100).toLocaleString("en-GB")}`;
  const feeField = (label: string, pence: number | null, suggestion: number | null, hint: string) => {
    const input = h("input", { type: "number", min: "0", step: "1", class: "input-short", "aria-label": `${label} (£, ex VAT)`, value: pence != null ? String(pence / 100) : "" });
    const use = suggestion && suggestion > 0
      ? h("button", { type: "button", class: "btn btn-quiet", onClick: () => (input.value = String(suggestion / 100)) }, `Use ${pounds(suggestion)}`)
      : "";
    return { input, row: h("div", { class: "inline-form" }, h("label", null, `${label} £ `, input), use, h("span", { class: "muted small" }, hint)) };
  };
  const auditFee = feeField("Audit fee", audit.audit_fee_pence, band.feePence, `About ${perMonth.toLocaleString("en-GB")} orders a month: ${band.label}.`);
  const implSuggest = annual !== null ? suggestedImplementationFee(annual) : null;
  const implFee = feeField("Implementation fee", audit.implementation_fee_pence, implSuggest,
    implSuggest ? `About ${Math.round(IMPLEMENTATION_SHARE * 100)}% of the first-year saving.` : "Run the levers to see a suggestion.");
  const cap = annual !== null ? retainerCap(annual) : null;
  const retainer = feeField("Retainer a month", audit.retainer_fee_pence, cap,
    cap ? `About ${pounds(cap)} a month: ${Math.round(RETAINER_CAP_SHARE * 100)}% of the monthly saving, to the nearest £25.` : "Run the levers to see a suggestion.");
  const parse = (el: HTMLInputElement, what: string) => {
    const v = el.value.trim();
    if (v === "") return null;
    if (!(Number(v) >= 0)) throw new Error(`The ${what} must be a number`);
    return Math.round(Number(v) * 100);
  };
  const saveFees = action("Save fees", async (_b, st) => {
    const r = parse(retainer.input, "retainer");
    if (r !== null && cap && r > cap) throw new Error(`The retainer is above the cap of ${pounds(cap)} a month`);
    await db.updateAudit(audit.id, {
      audit_fee_pence: parse(auditFee.input, "audit fee"),
      implementation_fee_pence: parse(implFee.input, "implementation fee"),
      retainer_fee_pence: r,
    });
    st.textContent = "Saved";
  });
  const missingFees = [audit.audit_fee_pence, audit.implementation_fee_pence, audit.retainer_fee_pence].some((x) => x == null);

  box.append(
    h("p", { class: "muted small first" }, "Our fees, ex VAT. Page 5 of the report sets the client's costs after the savings and these fees against what they pay today."),
    auditFee.row, implFee.row, retainer.row,
    h("div", { class: "row-actions" }, saveFees),
    missingFees ? notice("info", "Enter all three fees so the report shows the full position after our fees. Any fee left blank is named in the report and left out.") : "",
    !current(1) ? notice("info", "Run lever 1 on the current data before exporting.") : "",
    current(1) && !current(2) ? notice("warn", "Lever 2 has not been run on the current data, so the report will say so.") : "",
    sample ? notice("warn", "Sample rate cards were used. Do not send this report to a client.") : "",
    template
      ? h("p", { class: "muted small" }, "Built in the Ryoma Word template: a cover and five one-page parts (spend now, extra cost, savings, the plan, before and after). The recommendations are left for the founders to write.")
      : h("div", { class: "form-grid" }, h("label", null, "Template"), h("div", null, dropZone(tplFile), h("p", { class: "muted small" }, "The template is not deployed with the app, so choose ryoma-template.docx here."))),
    h("div", { class: "row-actions" }, exportBtn),
    out,
    ...(reports.length
      ? [h("h4", null, "Drafts exported"),
         table(["Draft", "When", "Engine", "Data", "Rate cards", "Packaging", ""],
           reports.map((r, i) => [String(reports.length - i), date(r.created_at), r.version_block.engineVersion, h("code", { title: r.version_block.datasetHash }, r.version_block.datasetHash.slice(0, 12)),
             r.version_block.rateCards.join(", "), r.version_block.packagingCatalogue ?? "–",
             deleteButton(`draft ${reports.length - i} from ${date(r.created_at)}`, `Delete the record of draft ${reports.length - i} (${date(r.created_at)})? The Word file you saved is not touched. Insights uses the first draft's date to judge whether the report was on time.`, async () => {
               await db.deleteReport(r.id);
               refresh();
             })]))]
      : []),
  );
  return box;
}

registerAuditSection(reportSection);
